import { useEffect, useState } from 'react';
import { fetchAllLicenses, submitRenewal } from '../services/api';
import {useSearchParams} from 'react-router-dom';

function statusBadge(status: string) {
  const s = (status || '').toLowerCase();
  const cls = s.includes('renouvel') || s.includes('expir') ? 'orange'
    : s.includes('valide') || s.includes('active') || s === 'valid' ? 'green'
    : s.includes('expired') ? 'red' : 'gray';
  return <span className={`badge ${cls}`}>{status || '–'}</span>;
}

function daysRemaining(expiryDate?: string): number | null {
  if (!expiryDate) return null;
  const diff = new Date(expiryDate).getTime() - Date.now();
  return Math.ceil(diff / 86_400_000);
}

export default function LicensesPage() {
  const [params]=useSearchParams();
  const [rows, setRows] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [renewingId, setRenewingId] = useState<string | null>(null);
  const [renewMsg, setRenewMsg] = useState('');

  useEffect(() => {
    fetchAllLicenses()
      .then(setRows)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleRenewal = async (licenseId: string) => {
    if (!licenseId) return;
    setRenewingId(licenseId);
    setRenewMsg('');
    try {
      await submitRenewal(licenseId);
      setRenewMsg('Demande de renouvellement transmise avec succès. En attente de validation ONT.');
      // Refresh list
      const updated = await fetchAllLicenses();
      setRows(updated);
    } catch (e: any) {
      setRenewMsg(e.message || 'Erreur lors du renouvellement');
    } finally {
      setRenewingId(null);
    }
  };

  if (error) return <div className="error-msg">{error}</div>;
  if (loading) return <div className="loading">Chargement...</div>;

  const active = rows.filter(r => r.status === 'valid' || r.status === 'active').length;
  const expiring = rows.filter(r => {
    const d = daysRemaining(r.expiry_date);
    return d != null && d > 0 && d <= 90;
  }).length;
  const expired = rows.filter(r => {
    const d = daysRemaining(r.expiry_date);
    return d != null && d <= 0;
  }).length;

  return (
    <div className="card">
      <div className="head">
        <div>
          <h3>Licences</h3>
          <div className="sub">Catégorie, durée de validité, dates et montant de renouvellement.</div>
        </div>
      </div>

      {renewMsg && (
        <div className={`notice ${renewMsg.includes('succès') ? '' : 'error'}`} style={{ marginTop: 12 }}>
          {renewMsg}
        </div>
      )}

      <div className="grid4" style={{ marginTop: 18 }}>
        <div className="kpi"><small>Total licences</small><strong>{rows.length}</strong></div>
        <div className="kpi"><small>Actives</small><strong style={{ color: 'var(--green, #22c55e)' }}>{active}</strong></div>
        <div className="kpi"><small>Expirant (90j)</small><strong style={{ color: 'var(--orange, #f59e0b)' }}>{expiring}</strong></div>
        <div className="kpi"><small>Expirées</small><strong style={{ color: 'var(--red, #ef4444)' }}>{expired}</strong></div>
      </div>

      <div className="table-wrap" style={{ marginTop: 18 }}>
        <table>
          <thead>
            <tr>
              <th>Établissement</th><th>Catégorie</th><th>N° licence</th><th>Début validité</th>
              <th>Durée</th><th>Expiration</th><th>Jours restants</th><th>Montant</th><th>Statut</th><th>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={10} style={{ textAlign: 'center', color: 'var(--muted)' }}>Aucune licence</td></tr>}
            {rows.map((r, i) => {
              const days = daysRemaining(r.expiry_date);
              const isPending = r.renewal_status === 'pending';
              const canRenew = ['valid','active','expiring','expired'].includes(r.status) && days != null && days <= 90 && !isPending;
              return (
                <tr key={r.id || i} style={params.get('license')===r.id?{background:'var(--bg)'}:undefined}>
                  <td>{r.establishment_name || '–'}</td>
                  <td>{r.category_code || r.category || '–'}</td>
                  <td>{r.license_number || '–'}</td>
                  <td>{r.validity_start_date || r.issue_date || '–'}</td>
                  <td><strong>{r.validity_months ? `${r.validity_months} mois` : '12 mois'}</strong></td>
                  <td>{r.expiry_date || '–'}</td>
                  <td style={{ color: days != null && days <= 30 ? 'var(--red, #ef4444)' : days != null && days <= 90 ? 'var(--orange, #f59e0b)' : undefined }}>
                    {days != null ? (days <= 0 ? 'Expiré' : `${days} j`) : '–'}
                  </td>
                  <td>{r.renewal_amount != null ? `${r.renewal_amount} ${r.currency || 'USD'}` : '–'}</td>
                  <td>
                    {statusBadge(r.status)}
                    {isPending && <span className="badge badge-warning" style={{ marginLeft: 6, fontSize: '0.75rem' }}>En attente</span>}
                  </td>
                  <td>
                    <button
                      className="btn primary"
                      disabled={!canRenew || renewingId !== null}
                      onClick={() => handleRenewal(r.id)}
                      title={isPending ? 'Renouvellement en cours de traitement' : (canRenew ? 'Renouveler la licence' : 'Disponible quand la licence expire dans 90 jours ou moins')}
                    >
                      {renewingId === r.id ? '...' : (isPending ? 'En cours' : 'Renouveler')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
